import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.vtovsuperfresh.com/api';

const SalaryPage = () => {
  const [tab, setTab] = useState('structures');
  const [structures, setStructures] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [payslipUrl, setPayslipUrl] = useState(null);
  const [payslipName, setPayslipName] = useState('');
  const [payslipLoading, setPayslipLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyStaff, setHistoryStaff] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pMonth, setPMonth] = useState(new Date().getMonth() + 1);
  const [pYear, setPYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({
    staffId: '', baseSalary: '', hra: '', da: '', ta: '', otherAllowances: '',
    pfDeduction: '', esiDeduction: '', taxDeduction: '', otherDeductions: ''
  });
  const toast = useToast();

  // Fetch payslip PDF as blob with auth headers
  const fetchPayslipBlob = async (payrollId) => {
    const role = localStorage.getItem('erp_role');
    const username = localStorage.getItem('erp_username');
    const res = await fetch(`${API_URL}/salary/payroll/${payrollId}/payslip`, {
      headers: {
        'x-role': role || '',
        'x-username': username || '',
      },
    });
    if (!res.ok) throw new Error('Failed to fetch payslip');
    return await res.blob();
  };

  const handleViewPayslip = async (p) => {
    setPayslipLoading(true);
    setPayslipName(`${p.full_name || p.username} — ${monthNames[p.month - 1]} ${p.year}`);
    setShowPayslipModal(true);
    try {
      const blob = await fetchPayslipBlob(p.id);
      const url = URL.createObjectURL(blob);
      setPayslipUrl(url);
    } catch {
      toast('Failed to load payslip', 'error');
      setShowPayslipModal(false);
    } finally {
      setPayslipLoading(false);
    }
  };

  const handleDownloadPayslip = async (p) => {
    try {
      const blob = await fetchPayslipBlob(p.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip_${(p.full_name || p.username).replace(/\s+/g, '_')}_${monthNames[p.month - 1]}_${p.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast('Payslip downloaded!', 'success');
    } catch {
      toast('Failed to download payslip', 'error');
    }
  };

  const closePayslipModal = () => {
    setShowPayslipModal(false);
    if (payslipUrl) {
      URL.revokeObjectURL(payslipUrl);
      setPayslipUrl(null);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const fetchStructures = useCallback(async () => {
    try {
      const { data } = await api.get('/salary/structures');
      setStructures(data.data);
    } catch { toast('Failed to load salary structures', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  const fetchPayroll = useCallback(async () => {
    try {
      const { data } = await api.get(`/salary/payroll?month=${pMonth}&year=${pYear}`);
      setPayroll(data.data);
    } catch { toast('Failed to load payroll', 'error'); }
    finally { setLoading(false); }
  }, [pMonth, pYear, toast]);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await api.get('/staff');
      setStaff(data.data);
    } catch {}
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    setLoading(true);
    if (tab === 'structures') fetchStructures();
    else fetchPayroll();
  }, [tab, fetchStructures, fetchPayroll]);

  const formatCurrency = (v) => `₹${parseFloat(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const openAddStructure = () => {
    setEditing(null);
    setForm({ staffId: '', baseSalary: '', hra: '', da: '', ta: '', otherAllowances: '', pfDeduction: '', esiDeduction: '', taxDeduction: '', otherDeductions: '' });
    setShowModal(true);
  };

  const openEditStructure = (s) => {
    setEditing(s);
    setForm({
      staffId: s.staff_id,
      baseSalary: s.base_salary,
      hra: s.hra, da: s.da, ta: s.ta, otherAllowances: s.other_allowances,
      pfDeduction: s.pf_deduction, esiDeduction: s.esi_deduction, taxDeduction: s.tax_deduction, otherDeductions: s.other_deductions
    });
    setShowModal(true);
  };

  const handleSaveStructure = async (e) => {
    e.preventDefault();
    if (!form.staffId || !form.baseSalary) {
      toast('Employee and base salary are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.post('/salary/structures', form);
      toast('Salary structure saved!', 'success');
      setShowModal(false);
      fetchStructures();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleGeneratePayroll = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/salary/payroll/generate', { month: pMonth, year: pYear });
      toast(data.message, 'success');
      setShowPayrollModal(false);
      fetchPayroll();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to generate payroll', 'error');
    } finally { setSaving(false); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.patch(`/salary/payroll/${id}/pay`);
      toast('Marked as paid!', 'success');
      fetchPayroll();
    } catch { toast('Failed to update', 'error'); }
  };

  const viewHistory = async (staffId, staffName) => {
    try {
      const { data } = await api.get(`/salary/history/${staffId}`);
      setHistoryData(data.data);
      setHistoryStaff(staffName);
      setShowHistoryModal(true);
    } catch { toast('Failed to load history', 'error'); }
  };

  const calcTotals = () => {
    const totalAllowances = (parseFloat(form.hra) || 0) + (parseFloat(form.da) || 0) + (parseFloat(form.ta) || 0) + (parseFloat(form.otherAllowances) || 0);
    const totalDeductions = (parseFloat(form.pfDeduction) || 0) + (parseFloat(form.esiDeduction) || 0) + (parseFloat(form.taxDeduction) || 0) + (parseFloat(form.otherDeductions) || 0);
    const gross = (parseFloat(form.baseSalary) || 0) + totalAllowances;
    const net = gross - totalDeductions;
    return { totalAllowances, totalDeductions, gross, net };
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  const totals = calcTotals();

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>💰 Salary & Payroll</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>Manage salary structures, generate payroll, and view history</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--dark-2)', borderRadius: 'var(--radius-sm)', padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {[{ key: 'structures', label: '💼 Salary Structures' }, { key: 'payroll', label: '📑 Payroll' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: tab === t.key ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.2s'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* STRUCTURES TAB */}
      {tab === 'structures' && (
        <div>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <button className="btn btn-primary" onClick={openAddStructure}>+ Add Salary Structure</button>
          </div>

          {structures.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">💰</div><p>No salary structures configured yet</p>
              <button className="btn btn-primary" onClick={openAddStructure}>+ Add Structure</button></div>
          ) : (
            <div className="card">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Role</th>
                      <th>Base Salary</th>
                      <th>Gross Salary</th>
                      <th>Deductions</th>
                      <th>Net Salary</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structures.map(s => (
                      <tr key={s.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: '#fff' }}>
                              {(s.full_name || s.username)[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>{s.full_name || s.username}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>@{s.username}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={`badge badge-${s.role}`}>{s.role}</span></td>
                        <td style={{ fontWeight: 700 }}>{formatCurrency(s.base_salary)}</td>
                        <td style={{ fontWeight: 700, color: 'var(--success)' }}>{formatCurrency(s.gross_salary)}</td>
                        <td style={{ fontWeight: 700, color: 'var(--danger)' }}>-{formatCurrency(parseFloat(s.pf_deduction) + parseFloat(s.esi_deduction) + parseFloat(s.tax_deduction) + parseFloat(s.other_deductions))}</td>
                        <td style={{ fontWeight: 800, fontSize: 15, color: 'var(--primary-light)' }}>{formatCurrency(s.net_salary)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEditStructure(s)}>✏️</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => viewHistory(s.staff_id, s.full_name || s.username)}>📜</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PAYROLL TAB */}
      {tab === 'payroll' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <select className="form-select" style={{ width: 160 }} value={pMonth} onChange={e => setPMonth(parseInt(e.target.value))}>
                {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select className="form-select" style={{ width: 100 }} value={pYear} onChange={e => setPYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={() => setShowPayrollModal(true)}>⚡ Generate Payroll</button>
          </div>

          {/* Payroll Stats */}
          {payroll.length > 0 && (
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card blue">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{payroll.length}</div>
                <div className="stat-label">Employees</div>
              </div>
              <div className="stat-card green">
                <div className="stat-icon">💰</div>
                <div className="stat-value">{formatCurrency(payroll.reduce((s, p) => s + parseFloat(p.net_salary), 0))}</div>
                <div className="stat-label">Total Payroll</div>
              </div>
              <div className="stat-card yellow">
                <div className="stat-icon">⏳</div>
                <div className="stat-value">{payroll.filter(p => p.payment_status === 'pending').length}</div>
                <div className="stat-label">Pending Payments</div>
              </div>
              <div className="stat-card green">
                <div className="stat-icon">✅</div>
                <div className="stat-value">{payroll.filter(p => p.payment_status === 'paid').length}</div>
                <div className="stat-label">Paid</div>
              </div>
            </div>
          )}

          {payroll.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📑</div><p>No payroll records for {monthNames[pMonth - 1]} {pYear}</p>
              <button className="btn btn-primary" onClick={() => setShowPayrollModal(true)}>⚡ Generate Payroll</button></div>
          ) : (
            <div className="card">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Net Pay</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payroll.map(p => (
                      <tr key={p.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{p.full_name || p.username}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.phone || '—'}</div>
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--success)' }}>{p.present_days}d</td>
                        <td style={{ fontWeight: 700, color: 'var(--danger)' }}>{p.absent_days}d</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(p.gross_salary)}</td>
                        <td style={{ fontWeight: 600, color: 'var(--danger)' }}>-{formatCurrency(p.total_deductions)}</td>
                        <td style={{ fontWeight: 800, fontSize: 15, color: 'var(--primary-light)' }}>{formatCurrency(p.net_salary)}</td>
                        <td>
                          <span className={`badge badge-${p.payment_status === 'paid' ? 'delivered' : 'pending'}`}>
                            {p.payment_status}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            {p.payment_status !== 'paid' && (
                              <button className="btn btn-success btn-sm" onClick={() => handleMarkPaid(p.id)} title="Mark as Paid">💳 Pay</button>
                            )}
                            {p.payment_status === 'paid' && (
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                {p.paid_at ? new Date(p.paid_at).toLocaleDateString('en-IN') : '—'}
                              </span>
                            )}
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleViewPayslip(p)}
                              title="View Payslip"
                              style={{ fontSize: 11 }}
                            >👁️ View</button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDownloadPayslip(p)}
                              title="Download Payslip PDF"
                              style={{ fontSize: 11 }}
                            >📥 PDF</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Salary Structure Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Salary Structure' : 'Add Salary Structure'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveStructure}>
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Employee *</label>
                <select className="form-select" value={form.staffId} onChange={e => setForm(p => ({ ...p, staffId: e.target.value }))} disabled={!!editing}>
                  <option value="">Select employee...</option>
                  {staff.filter(s => s.role !== 'ADM').map(s => (
                    <option key={s.id} value={s.id}>{s.full_name || s.username} ({s.role})</option>
                  ))}
                </select>
              </div>

              {/* Earnings section */}
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)', marginBottom: 12 }}>💰 Earnings</h4>
                <div className="form-grid form-grid-2" style={{ gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Base Salary *</label>
                    <input className="form-input" type="number" placeholder="0" value={form.baseSalary} onChange={e => setForm(p => ({ ...p, baseSalary: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">HRA</label>
                    <input className="form-input" type="number" placeholder="0" value={form.hra} onChange={e => setForm(p => ({ ...p, hra: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">DA (Dearness Allowance)</label>
                    <input className="form-input" type="number" placeholder="0" value={form.da} onChange={e => setForm(p => ({ ...p, da: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">TA (Travel Allowance)</label>
                    <input className="form-input" type="number" placeholder="0" value={form.ta} onChange={e => setForm(p => ({ ...p, ta: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Other Allowances</label>
                    <input className="form-input" type="number" placeholder="0" value={form.otherAllowances} onChange={e => setForm(p => ({ ...p, otherAllowances: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Deductions section */}
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)', marginBottom: 12 }}>📉 Deductions</h4>
                <div className="form-grid form-grid-2" style={{ gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">PF (Provident Fund)</label>
                    <input className="form-input" type="number" placeholder="0" value={form.pfDeduction} onChange={e => setForm(p => ({ ...p, pfDeduction: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ESI</label>
                    <input className="form-input" type="number" placeholder="0" value={form.esiDeduction} onChange={e => setForm(p => ({ ...p, esiDeduction: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tax (TDS)</label>
                    <input className="form-input" type="number" placeholder="0" value={form.taxDeduction} onChange={e => setForm(p => ({ ...p, taxDeduction: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Other Deductions</label>
                    <input className="form-input" type="number" placeholder="0" value={form.otherDeductions} onChange={e => setForm(p => ({ ...p, otherDeductions: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div style={{ background: 'var(--dark-3)', borderRadius: 'var(--radius-sm)', padding: 16, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, textAlign: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>GROSS SALARY</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--success)' }}>{formatCurrency(totals.gross)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>TOTAL DEDUCTIONS</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--danger)' }}>-{formatCurrency(totals.totalDeductions)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>NET SALARY</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary-light)' }}>{formatCurrency(totals.net)}</div>
                  </div>
                </div>
              </div>

              <div className="modal-footer" style={{ margin: 0, padding: 0, border: 'none' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Structure'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Payroll Modal */}
      {showPayrollModal && (
        <div className="modal-overlay" onClick={() => setShowPayrollModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">⚡ Generate Payroll</h3>
              <button className="modal-close" onClick={() => setShowPayrollModal(false)}>✕</button>
            </div>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📑</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Generate payroll for</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary-light)', marginBottom: 16 }}>
                {monthNames[pMonth - 1]} {pYear}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
                This will calculate salary based on attendance records and salary structures for all active employees.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => setShowPayrollModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleGeneratePayroll} disabled={saving}>
                  {saving ? 'Generating...' : '⚡ Generate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salary History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📜 Salary History — {historyStaff}</h3>
              <button className="modal-close" onClick={() => setShowHistoryModal(false)}>✕</button>
            </div>
            {historyData.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No salary history found</div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Present</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Net Pay</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map(h => (
                      <tr key={h.id}>
                        <td style={{ fontWeight: 700 }}>{monthNames[h.month - 1]} {h.year}</td>
                        <td>{h.present_days}d / {h.working_days}d</td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(h.gross_salary)}</td>
                        <td style={{ color: 'var(--danger)', fontWeight: 600 }}>-{formatCurrency(h.total_deductions)}</td>
                        <td style={{ fontWeight: 800, color: 'var(--primary-light)' }}>{formatCurrency(h.net_salary)}</td>
                        <td>
                          <span className={`badge badge-${h.payment_status === 'paid' ? 'delivered' : 'pending'}`}>
                            {h.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Payslip Preview Modal */}
      {showPayslipModal && (
        <div className="modal-overlay" onClick={closePayslipModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3 className="modal-title">👁️ Payslip Preview — {payslipName}</h3>
              <button className="modal-close" onClick={closePayslipModal}>✕</button>
            </div>
            <div style={{ flex: 1, position: 'relative', background: 'var(--dark-3)', borderRadius: 8, overflow: 'hidden' }}>
              {payslipLoading ? (
                <div className="flex-center" style={{ height: '100%' }}>
                  <div className="spinner"></div>
                  <p style={{ marginLeft: 12, fontWeight: 600 }}>Generating PDF...</p>
                </div>
              ) : payslipUrl ? (
                <iframe
                  src={`${payslipUrl}#toolbar=0`}
                  title="Payslip Preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div className="flex-center" style={{ height: '100%', color: 'var(--danger)' }}>
                  ⚠️ Failed to load PDF preview.
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={closePayslipModal}>Close</button>
              {payslipUrl && (
                <button className="btn btn-primary" onClick={() => {
                  const a = document.createElement('a');
                  a.href = payslipUrl;
                  a.download = `Payslip_${payslipName.replace(/\s+/g, '_')}.pdf`;
                  a.click();
                }}>📥 Download PDF</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryPage;
